import { z } from "zod";

// Verdict enums
export enum Verdict {
  Supported = "supported",
  PartiallySupported = "partially_supported",
  Contradicted = "contradicted",
  NotFound = "not_found",
}

export enum InternalVerdict {
  Supported = "supported",
  Unrelated = "unrelated",
}

export enum EvidenceSource {
  Internal = "internal",
  External = "external",
  Hybrid = "hybrid",
}

export const UsedFactSchema = z.object({
  label: z
    .string()
    .describe("User-friendly fact label, e.g. 'Mission / Vision'"),
  content: z.string().describe("Fact content (truncated if long)"),
});

export const FactCheckRequestSchema = z.object({
  content: z.string().describe("The text content to be fact-checked"),
  usedFacts: z
    .array(UsedFactSchema)
    .optional()
    .describe("Optional list of organization facts provided directly"),
});

export const FactUsedOutSchema = z.object({
  label: z.string(),
  content: z.string(),
  score: z.number().optional(),
});

export const ExternalReferenceOutSchema = z.object({
  claimId: z.string(),
  urls: z.array(z.url()),
});

export const FindingOutSchema = z.object({
  textSpanId: z.string(),
  original: z.string(),
  verdict: z.enum(Verdict),
  confidence: z.number().min(0).max(1),
  explanation: z.string().optional(),
  keySources: z.array(z.string()).optional(),
  links: z.array(z.url()).optional(),
  evidenceSource: z.enum(EvidenceSource).optional(),
});

export const FactCheckResponseSchema = z.object({
  factsUsed: z.array(FactUsedOutSchema),
  externalReferences: z.array(ExternalReferenceOutSchema),
  findings: z.array(FindingOutSchema),
  score: z
    .number()
    .min(0)
    .max(10)
    .optional()
    .describe("Overall accuracy score (0–10)"),
  summary: z
    .string()
    .optional()
    .describe("Concise summary of the fact-check results"),
  error: z.string().optional().describe("Error message if fact-check failed"),
});

export const ClaimSchema = z.object({
  id: z.string(),
  text: z.string(),
  entity: z
    .string()
    .nullable()
    .describe("Main subject (person, place, organization, event, etc.)"),
  category: z
    .string()
    .nullable()
    .describe(
      "Type of fact (person, organization, place, object, event, time, quantity, etc.)",
    ),
  topic: z
    .string()
    .nullable()
    .describe("General grouping (e.g. surf-spot, business, education)"),
});

export const InternalCheckResultSchema = z.object({
  claimId: z.string(),
  claimText: z.string(),
  facts: z.array(
    z.object({
      label: z.string(),
      content: z.string(),
      score: z.number(),
    }),
  ),
  bestMatchScore: z.number(),
  verdict: z.enum(InternalVerdict),
  explanation: z.string(),
});

export const ExternalCheckResultSchema = z.object({
  claimId: z.string(),
  urls: z.array(z.string()),
  snippets: z.array(z.string()),
});

export const VerifiedClaimSchema = z.object({
  claimId: z.string().optional(),
  verdict: z.enum(Verdict),
  confidence: z.number().min(0).max(1),
  explanation: z.string(),
  keySources: z.array(z.string()).optional(),
  evidenceSource: z.enum(EvidenceSource),
});

export const OrgContextSchema = z.object({
  organizationName: z.string().optional(),
});

export const FactCheckTaskResultSchema = z.object({
  claims: z.array(ClaimSchema),
  internalResults: z.array(InternalCheckResultSchema),
  externalResults: z.array(ExternalCheckResultSchema),
  verifiedClaims: z.array(VerifiedClaimSchema),
});

export const ClaimToVerifySchema = z.object({
  claim: z.string(),
  claimId: z.string().optional(),
  internalFacts: z.array(z.string()),
  externalSnippets: z.array(z.string()),
  urls: z.array(z.string()),
  evidenceSource: z.enum(EvidenceSource),
});

export type UsedFact = z.infer<typeof UsedFactSchema>;
export type FactCheckRequest = z.infer<typeof FactCheckRequestSchema>;
export type ClaimToVerify = z.infer<typeof ClaimToVerifySchema>;
export type FactUsedOut = z.infer<typeof FactUsedOutSchema>;
export type ExternalReferenceOut = z.infer<typeof ExternalReferenceOutSchema>;
export type FindingOut = z.infer<typeof FindingOutSchema>;
export type FactCheckResponse = z.infer<typeof FactCheckResponseSchema>;
export type Claim = z.infer<typeof ClaimSchema>;
export type InternalCheckResult = z.infer<typeof InternalCheckResultSchema>;
export type ExternalCheckResult = z.infer<typeof ExternalCheckResultSchema>;
export type VerifiedClaim = z.infer<typeof VerifiedClaimSchema>;
export type OrgContext = z.infer<typeof OrgContextSchema>;
export type FactCheckTaskResult = z.infer<typeof FactCheckTaskResultSchema>;

export const similarityThresholdHigh = 0.6;
export const similarityThresholdLow = 0.45;
export const maxClaimVerificationWorkers = 5;
